/**
 * Document Generation Service
 * Utilities for converting markdown content to HTML and downloading as DOCX/PDF
 */

/**
 * Convert markdown tables to HTML tables
 */
export const convertMarkdownTablesToHTML = (text: string): string => {
  // Regex to match markdown tables
  const tableRegex = /(\|[^\n]+\|[\r\n]+\|[-:\s|]+\|[\r\n]+(?:\|[^\n]+\|[\r\n]*)+)/g;
  
  return text.replace(tableRegex, (match) => {
    const lines = match.trim().split(/[\r\n]+/);
    if (lines.length < 3) return match; // Need at least header, separator, and one row
    
    // Parse header
    const headers = lines[0].split('|').map(h => h.trim()).filter(h => h);
    
    // Skip separator line (lines[1])
    
    // Parse body rows
    const rows = lines.slice(2).map(line => 
      line.split('|').map(cell => cell.trim()).filter(cell => cell !== '')
    );
    
    // Build HTML table
    let html = '<table style="border-collapse: collapse; width: auto;">';
    
    // Add header
    html += '<thead><tr>';
    headers.forEach(header => {
      html += `<th style="border: 1px solid #d1d5db; padding: 0.75em 1em; text-align: left; font-weight: bold; background-color: transparent;">${header}</th>`;
    });
    html += '</tr></thead>';
    
    // Add body
    html += '<tbody>';
    rows.forEach(row => {
      if (row.length > 0) {
        html += '<tr>';
        row.forEach(cell => {
          html += `<td style="border: 1px solid #d1d5db; padding: 0.75em 1em; background-color: transparent;">${cell}</td>`;
        });
        html += '</tr>';
      }
    });
    html += '</tbody></table>';
    
    return html;
  });
};

/**
 * Convert markdown content to HTML with full formatting
 */
export const convertMarkdownToHTML = (content: string): string => {
  let html = content;

  // Normalize line endings: CRLF and lone CR → LF
  // Required so that multiline regex ^ anchors work correctly regardless of platform or API source
  html = html.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Clean up content: remove base64 images and replace with placeholder
  html = html
    .replace(/!\[Generated Image\]\(data:image\/png;base64,[A-Za-z0-9+/=]+\)/g, '[Generated Image]')
    .replace(/\[Download\s+([^\]]+)\]\(analysisfile:[^:]+:[^:]+:([^)]+)\)/g, (match: string, label: string, encodedFileName: string) => {
      try {
        const fileName = decodeURIComponent(encodedFileName);
        return `File: ${fileName}`;
      } catch {
        return `File: ${label}`;
      }
    })
    .replace(/\[__REPLACE__\]/g, '');
  
  // Strip thinking content and HTML img tags
  html = html.replace(/<thinking>[\s\S]*?<\/thinking>/gi, '').replace(/<\/?thinking[^>]*>/gi, '');
  html = html.replace(/<img[^>]*>\s*/gi, '');
  
  // Convert markdown to HTML (order matters!)
  
  // 1. Code blocks first (to protect their content from other conversions)
  html = html.replace(/```[\w]*\n([\s\S]*?)```/g, '<pre><code>$1</code></pre>');
  
  // 2. Convert markdown tables to HTML tables
  html = convertMarkdownTablesToHTML(html);
  // Ensure closing </table> is always followed by a newline so that header/list
  // regexes with ^ anchors can still match content immediately after a table.
  html = html.replace(/<\/table>(?!\n)/g, '</table>\n');

  // 3. Headers
  html = html.replace(/^#### (.+)$/gm, '<h4>$1</h4>');
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');
  
  // 4. Horizontal rules
  html = html.replace(/^---$/gm, '<hr>');
  
  // 5. Bold (BEFORE lists to handle bold inside lists)
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
  
  // 6. Italic
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/_([^_]+?)_/g, '<em>$1</em>');
  
  // 7. Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  
  // 8. Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
  
  // 9. Lists - convert to <li> tags
  html = html.replace(/^[*-] (.+)$/gm, '<li>$1</li>');
  html = html.replace(/^\d+\. (.+)$/gm, '<liOL>$1</liOL>');
  
  // 10. Paragraphs
  html = html.replace(/\n\n+/g, '</p><p>');
  html = html.replace(/\n/g, '<br>');
  html = '<p>' + html + '</p>';
  
  // 11. Wrap consecutive list items
  html = html.replace(/(<li>[^]*?<\/li>(<br>)?)+/g, (match) => {
    const cleaned = match.replace(/<br>/g, '');
    return `<ul>${cleaned}</ul>`;
  });
  html = html.replace(/(<liOL>[^]*?<\/liOL>(<br>)?)+/g, (match) => {
    const cleaned = match.replace(/<br>/g, '');
    const withLi = cleaned.replace(/<liOL>/g, '<li>').replace(/<\/liOL>/g, '</li>');
    return `<ol>${withLi}</ol>`;
  });
  
  // 12. Clean up
  html = html.replace(/<p><\/p>/g, '');
  html = html.replace(/<p>\s*<\/p>/g, '');
  html = html.replace(/<\/ul>\s*<ul>/g, '');
  html = html.replace(/<\/ol>\s*<ol>/g, '');
  html = html.replace(/<p>(<table[\s\S]*?<\/table>)<\/p>/g, '$1');
  html = html.replace(/<p>(<h[1-6]>[\s\S]*?<\/h[1-6]>)<\/p>/g, '$1');
  html = html.replace(/<p>(<ul>[\s\S]*?<\/ul>)<\/p>/g, '$1');
  html = html.replace(/<p>(<ol>[\s\S]*?<\/ol>)<\/p>/g, '$1');
  html = html.replace(/<p>(<pre>[\s\S]*?<\/pre>)<\/p>/g, '$1');

  // Remove spurious <br> tags that appear immediately after block-level closing tags
  // e.g. </h3><br><ul> produces an extra blank line in Word — strip it.
  html = html.replace(/(<\/h[1-6]>|<\/table>|<\/ul>|<\/ol>|<\/pre>|<\/p>)(<br>)+/g, '$1');
  html = html.replace(/(<br>)+(<h[1-6]>|<ul>|<ol>|<table|<pre>)/g, '$2');
  
  return html;
};

/**
 * Create HTML document with proper styling
 */
export const createHTMLDocument = (content: string, title: string): string => {
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>${title}</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            font-size: 12pt; 
            line-height: 1.6; 
            max-width: 800px; 
            margin: 40px auto; 
            padding: 20px;
          }
          h1 { color: #333; margin-bottom: 20px; }
          h2, h3, h4 { color: #333; margin-top: 20px; margin-bottom: 10px; }
          pre { 
            background-color: #f4f4f4; 
            padding: 10px; 
            border-radius: 4px; 
            overflow-x: auto;
          }
          code { 
            background-color: #f4f4f4; 
            padding: 2px 4px; 
            border-radius: 2px; 
            font-family: 'Courier New', monospace;
          }
          table { 
            border-collapse: collapse; 
            width: 100%; 
            margin: 10px 0; 
          }
          th, td { 
            border: 1px solid #ddd; 
            padding: 8px; 
            text-align: left; 
          }
          th { 
            background-color: #f4f4f4; 
            font-weight: bold;
          }
          ul, ol { margin: 10px 0; padding-left: 30px; }
          li { margin: 5px 0; }
          a { color: #0066cc; text-decoration: none; }
          a:hover { text-decoration: underline; }
          hr { border: none; border-top: 1px solid #ddd; margin: 20px 0; }
          @media print {
            body { margin: 0; padding: 20px; }
          }
        </style>
      </head>
      <body>
        <h1>${title} - ${new Date().toLocaleDateString()}</h1>
        ${content}
      </body>
    </html>
  `;
};

/**
 * Download content as Word document (.doc format)
 */
export const downloadAsDocx = (htmlContent: string, filename: string): void => {
  try {
    const blob = new Blob([htmlContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename.endsWith('.doc') ? filename : `${filename}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to download as DOC:', error);
    throw error;
  }
};

/**
 * Download content as HTML file (for PDF conversion via print)
 */
export const downloadAsHTML = (htmlContent: string, filename: string): void => {
  try {
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename.endsWith('.html') ? filename : `${filename}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
  } catch (error) {
    console.error('Failed to download as HTML:', error);
    throw error;
  }
};

/**
 * Render HTML content to a real PDF using jsPDF + html2canvas and open it in
 * a new browser tab so the user sees the native PDF viewer immediately.
 * Falls back to HTML download if anything goes wrong.
 */
export const printAsPDF = async (
  htmlContent: string,
  filename: string = `report-${Date.now()}`
): Promise<void> => {
  try {
    const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
      import('jspdf'),
      import('html2canvas'),
    ]);

    // Mount a hidden iframe so we can measure & render the full HTML
    const iframe = document.createElement('iframe');
    Object.assign(iframe.style, {
      position: 'fixed',
      top: '0',
      left: '-9999px',
      width: '900px',   // A4-ish width in px at 96 dpi
      height: '1px',
      border: 'none',
      visibility: 'hidden',
    });
    document.body.appendChild(iframe);

    await new Promise<void>((resolve) => {
      iframe.onload = () => resolve();
      iframe.srcdoc = htmlContent;
    });

    const iframeDoc = iframe.contentDocument!;
    const body = iframeDoc.body;
    const scrollHeight = Math.max(body.scrollHeight, body.offsetHeight);
    iframe.style.height = `${scrollHeight}px`;

    // Give fonts/images a moment to settle
    await new Promise((r) => setTimeout(r, 400));

    const canvas = await html2canvas(body, {
      scale: 1,           // 2× for sharper text
      useCORS: true,
      backgroundColor: '#ffffff',
      scrollX: 0,
      scrollY: 0,
      windowWidth: 1000,
      windowHeight: scrollHeight,
    });

    document.body.removeChild(iframe);

    // A4 dimensions in mm
    const A4_W = 210;
    const A4_H = 297;
    const margin = 10; // mm
    const usableW = A4_W - margin * 2;

    const imgW = canvas.width;
    const imgH = canvas.height;
    const pxPerMm = imgW / usableW;        // canvas px per mm
    const pageHeightPx = A4_H * pxPerMm;  // how many canvas px fit on one page

    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

    let yOffset = 0;
    let page = 0;
    while (yOffset < imgH) {
      if (page > 0) pdf.addPage();

      // Slice the canvas for this page
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = imgW;
      sliceCanvas.height = Math.min(pageHeightPx, imgH - yOffset);
      const ctx = sliceCanvas.getContext('2d')!;
      ctx.drawImage(canvas, 0, -yOffset);

      const imgData = sliceCanvas.toDataURL('image/jpeg', 0.95);
      const sliceHeightMm = (sliceCanvas.height / pxPerMm);
      pdf.addImage(imgData, 'JPEG', margin, margin, usableW, sliceHeightMm);

      yOffset += pageHeightPx;
      page++;
    }

    // Open as blob URL in a new tab — browser shows native PDF viewer
    const blob = pdf.output('blob');
    const url = URL.createObjectURL(blob);
    const tab = window.open(url, '_blank');
    if (!tab) {
      // Popup blocked — trigger download instead
      const link = document.createElement('a');
      link.href = url;
      link.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    // Revoke after a short delay to allow the tab to load
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch (error) {
    console.error('[PDF] Generation failed, falling back to HTML download:', error);
    downloadAsHTML(htmlContent, filename);
  }
};

/**
 * Render a live DOM element directly to PDF using html2canvas.
 * Clones the element offscreen at a fixed A4-friendly width so content
 * is never clipped by the chat column width. Supports light/dark theme override.
 */
export const printDomElementAsPDF = async (
  html: string,
  filename: string = `report-${Date.now()}`,
  theme: 'light' | 'dark' = 'light'
): Promise<void> => {
  try {
    const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
      import('jspdf'),
      import('html2canvas'),
    ]);

    // A4 at 96 dpi ≈ 794px wide. Use full A4 width — no side gutters.
    const PDF_WIDTH_PX = 794;

    // Build the clone entirely from the stored HTML string — never touch the live
    // DOM element so the visible card on screen is not affected during export.
    const wrapper = document.createElement('div');
    Object.assign(wrapper.style, {
      position: 'fixed',
      top: '0',
      left: '-9999px',
      width: `${PDF_WIDTH_PX}px`,
      background: theme === 'dark' ? '#19191C' : '#ffffff',
      zIndex: '-1',
      pointerEvents: 'none',
    });

    const parser = new DOMParser();
    const parsed = parser.parseFromString(html, 'text/html');
    const article = parsed.querySelector('article');
    const contentHtml = article ? article.outerHTML : html;

    const clone = document.createElement('div');
    clone.className = 'jira-card-content';
    clone.setAttribute('data-mode', theme);
    clone.style.width = `${PDF_WIDTH_PX}px`;
    clone.style.maxWidth = 'none';
    clone.style.overflow = 'visible';
    clone.innerHTML = contentHtml;

    clone.querySelectorAll<HTMLElement>('section, article, .field-description').forEach((el) => {
      el.style.paddingBottom = '24px';
      el.style.breakInside = 'avoid';
    });

    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    const isLight = theme === 'light';

    const TEXT_COLOR  = isLight ? '#172B4D'          : '#e5e5e5';
    const DD_COLOR    = isLight ? '#084ca5'           : '#82B6FA';
    const H1_A_COLOR  = isLight ? '#0052CC'           : '#82B6FA';
    const H2_BG       = isLight ? '#ffffff'           : '#19191C';
    const P_BG        = isLight ? '#ffffff'           : '#313136';
    const P_BORDER    = isLight ? '1px solid #d1d5db' : '1px solid #75757E';
    const ARTICLE_BG  = isLight ? '#f5f5f5'           : '#424248';
    const CARD_BG     = isLight ? 'rgb(242 242 242)'  : '#424248';

    const sp = (el: HTMLElement, prop: string, val: string) =>
      el.style.setProperty(prop, val, 'important');

    clone.querySelectorAll<HTMLElement>('article').forEach(el => {
      sp(el, 'background-color', ARTICLE_BG);
    });
    clone.querySelectorAll<HTMLElement>('.jira-issue-card').forEach(el => {
      sp(el, 'background-color', CARD_BG);
    });
    clone.querySelectorAll<HTMLElement>('h1').forEach(el => {
      sp(el, 'color', TEXT_COLOR);
      sp(el, 'line-height', '1.2');
    });
    clone.querySelectorAll<HTMLElement>('h1 a').forEach(el => {
      sp(el, 'color', H1_A_COLOR);
      sp(el, 'line-height', '1.2');
    });
    clone.querySelectorAll<HTMLElement>('h3').forEach(el => {
      sp(el, 'color', TEXT_COLOR);
      sp(el, 'line-height', '1.2');
    });
    clone.querySelectorAll<HTMLElement>('dt').forEach(el => {
      sp(el, 'color', TEXT_COLOR);
      sp(el, 'line-height', '1.5');
      sp(el, 'align-self', 'start');
    });

    const BADGE_CLASSES = ['health-green', 'health-yellow', 'health-red', 'status-badge', 'status-default'];
    const BADGE_SEL = BADGE_CLASSES.map(c => `.${c}`).join(', ');

    clone.querySelectorAll<HTMLElement>('span, a, li').forEach(el => {
      const parentIsBadge = BADGE_CLASSES.some(c => el.parentElement?.classList.contains(c));
      if (!parentIsBadge) {
        sp(el, 'line-height', '22px');
      }
    });

    clone.querySelectorAll<HTMLElement>('h2').forEach(el => {
      sp(el, 'background-color', H2_BG);
      sp(el, 'color', TEXT_COLOR);
      sp(el, 'display', 'block');
      sp(el, 'line-height', 'normal');
      sp(el, 'padding-top', '4px');
      sp(el, 'padding-bottom', '4px');
      sp(el, 'text-align', 'center');
      const span = document.createElement('span');
      span.style.setProperty('display', 'block', 'important');
      span.style.setProperty('position', 'relative', 'important');
      span.style.setProperty('top', '-8px', 'important');
      span.innerHTML = el.innerHTML;
      el.innerHTML = '';
      el.appendChild(span);
    });

    clone.querySelectorAll<HTMLElement>('p').forEach(el => {
      sp(el, 'background-color', P_BG);
      sp(el, 'color', TEXT_COLOR);
      sp(el, 'border', P_BORDER);
      sp(el, 'line-height', 'normal');
      sp(el, 'padding-top', '4px');
      sp(el, 'padding-bottom', '4px');
      const span = document.createElement('span');
      span.style.setProperty('display', 'block', 'important');
      span.style.setProperty('position', 'relative', 'important');
      span.style.setProperty('top', '-6px', 'important');
      span.innerHTML = el.innerHTML;
      el.innerHTML = '';
      el.appendChild(span);
    });

    const applyBadgeCenter = (el: HTMLElement) => {
      sp(el, 'margin-top', '4px');
      sp(el, 'padding-top', '0px');
      sp(el, 'padding-bottom', '12px');
      sp(el, 'line-height', 'normal');
    };

    clone.querySelectorAll<HTMLElement>('.health-green').forEach(el => {
      applyBadgeCenter(el);
      sp(el, 'color', 'white');
    });

    clone.querySelectorAll<HTMLElement>('.health-yellow').forEach(el => {
      applyBadgeCenter(el);
      sp(el, 'color', 'white');
    });

    clone.querySelectorAll<HTMLElement>('.health-red').forEach(el => {
      applyBadgeCenter(el);
      sp(el, 'color', isLight ? '#172B4D' : '#e5e5e5');
    });

    clone.querySelectorAll<HTMLElement>('.status-badge').forEach(el => {
      applyBadgeCenter(el);
    });

    clone.querySelectorAll<HTMLElement>('.status-default').forEach(el => {
      applyBadgeCenter(el);
    });

    clone.querySelectorAll<HTMLElement>('dd').forEach(el => {
      const hasBadgeClass  = BADGE_CLASSES.some(c => el.classList.contains(c));
      const hasBadgeChild  = !!el.querySelector(BADGE_SEL);
      if (hasBadgeClass) {
        applyBadgeCenter(el);
        if (el.classList.contains('health-green') || el.classList.contains('health-yellow')) {
          sp(el, 'color', 'white');
        } else if (el.classList.contains('health-red')) {
          sp(el, 'color', isLight ? '#172B4D' : '#e5e5e5');
        }
      } else if (hasBadgeChild) {
        sp(el, 'display', 'inline');
        sp(el, 'line-height', 'normal');
      } else {
        sp(el, 'color', DD_COLOR);
        sp(el, 'line-height', '22px');
      }
    });

    sp(wrapper, 'transform', 'none');
    sp(wrapper, 'filter', 'none');
    clone.querySelectorAll<HTMLElement>('*').forEach(el => {
      sp(el, 'transform', 'none');
      sp(el, 'filter', 'none');
      sp(el, 'backdrop-filter', 'none');
      sp(el, 'text-rendering', 'auto');
      sp(el, 'border-radius', '0');
    });

    await (document as any).fonts?.ready;
    await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);

    const totalHeight = clone.scrollHeight;

    const exportScale = Math.max(2, Math.ceil(window.devicePixelRatio));

    const canvas = await html2canvas(clone, {
      scale: exportScale,
      useCORS: true,
      foreignObjectRendering: false,
      backgroundColor: theme === 'dark' ? '#19191C' : '#ffffff',
      scrollX: 0,
      scrollY: 0,
      windowWidth: PDF_WIDTH_PX,
      windowHeight: totalHeight,
      width: PDF_WIDTH_PX,
      height: totalHeight,
    });

    const linkAnnotations: { url: string; canvasX: number; canvasY: number; canvasW: number; canvasH: number }[] = [];
    const wrapperRect = wrapper.getBoundingClientRect();
    clone.querySelectorAll<HTMLAnchorElement>('a[href]').forEach(a => {
      const href = a.getAttribute('href');
      if (
        !href ||
        href.startsWith('#') ||
        !(href.startsWith('http:') || href.startsWith('https:') || href.startsWith('mailto:'))
      ) return;
      const rect = a.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      linkAnnotations.push({
        url: a.href,
        canvasX: (rect.left - wrapperRect.left) * exportScale,
        canvasY: (rect.top  - wrapperRect.top)  * exportScale,
        canvasW: rect.width  * exportScale,
        canvasH: rect.height * exportScale,
      });
    });

    document.body.removeChild(wrapper);

    const imgW = canvas.width;
    const imgH = canvas.height;

    // Use exact pt units for standard A4
    const A4_W_PT = 595.28;
    const A4_H_PT = 841.89;

    // How many pt does one canvas pixel represent?
    const ratio = A4_W_PT / imgW;

    // To prevent rounding errors, we'll work primarily in pixels for slicing
    // and convert to points only for the PDF addImage call.
    
    // Calculate exact pixel height of one A4 page
    const pxPerPage = Math.floor(A4_H_PT / ratio);

    const pdf = new jsPDF({ unit: 'pt', format: [A4_W_PT, A4_H_PT], orientation: 'portrait' });

    let yOffset = 0;
    let page = 0;

    while (yOffset < imgH) {
      // If remaining content is negligible (less than 1px), stop.
      if (yOffset >= imgH - 1) break;

      // Determine slice height for this page in pixels in integers.
      // We always take a full page of pixels, or whatever is remaining.
      const availableHeight = Math.min(pxPerPage, imgH - yOffset);
      
      // Safety: ensure we don't try to create a 0-height canvas
      if (availableHeight <= 0) break;

      if (page > 0) {
        pdf.addPage([A4_W_PT, A4_H_PT]);
      }

      // Paint background for the whole page first, so empty areas on partial pages match theme
      const bgHex = theme === 'dark' ? '#424248' : '#f2f2f2';
      pdf.setFillColor(bgHex);
      pdf.rect(0, 0, A4_W_PT, A4_H_PT, 'F');
      
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width  = imgW;
      sliceCanvas.height = availableHeight;

      // Extract exact slice using source coordinates
      const ctx = sliceCanvas.getContext('2d')!;
      ctx.drawImage(
        canvas, 
        0, yOffset, imgW, availableHeight, 
        0, 0, imgW, availableHeight
      );

      const imgData = sliceCanvas.toDataURL('image/jpeg', 0.95);
      
      // Height in points for this specific slice
      const sliceHeightPt = availableHeight * ratio;
      
      // Add the image. If it's the last page and partial, it will naturally
      // be shorter than the page height. jsPDF handles the rest.
      pdf.addImage(imgData, 'JPEG', 0, 0, A4_W_PT, sliceHeightPt);

      yOffset += availableHeight;
      page++;
    }

    linkAnnotations.forEach(link => {
      const yPt = link.canvasY * ratio;
      const pageIndex = Math.floor(yPt / A4_H_PT);
      const yOnPage  = yPt - pageIndex * A4_H_PT;
      pdf.setPage(pageIndex + 1);
      pdf.link(
        link.canvasX * ratio,
        yOnPage,
        link.canvasW * ratio,
        link.canvasH * ratio,
        { url: link.url }
      );
    });

    const blob = pdf.output('blob');
    const url = URL.createObjectURL(blob);
    const tab = window.open(url, '_blank');
    if (!tab) {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch (error) {
    console.error('[PDF] DOM capture failed:', error);
    throw error;
  }
};

/**
 * Detect if content is a Jira issue card HTML payload
 */
const isJiraIssueCardHtml = (content: string): boolean =>
  content.includes('class="jira-issue-card"') || content.includes("class='jira-issue-card'");

const escapeHtml = (input: string): string =>
  input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * Convert <dl class="field-grid"> blocks to simple bold-key paragraphs:
 *   <p><strong>Key:</strong> Value</p>
 * This is the most reliable format for both PDF (print) and Word (.doc).
 * Used for Jira issue cards since their field grids can contain complex nested HTML that doesn't render well in exports.
 */
const transformFieldGrids = (html: string): string =>
  html.replace(
    /<dl[^>]*class="[^"]*field-grid[^"]*"[^>]*>([\s\S]*?)<\/dl>/gi,
    (_match, inner) => {
      const pairRegex = /<dt[^>]*>([\s\S]*?)<\/dt>\s*<dd([^>]*)>([\s\S]*?)<\/dd>/gi;
      let result = '';
      let pair: RegExpExecArray | null;
      while ((pair = pairRegex.exec(inner)) !== null) {
        const key = escapeHtml(pair[1]).trim();
        const val = escapeHtml(pair[3]).trim();
        result += `<p style="margin:4px 0;"><strong>${key}:</strong> ${val}</p>\n`;
      }
      return result;
    }
  );

/**
 * Build a standalone HTML document for a Jira issue card.
 * Field grids are pre-converted to bold-key paragraphs for reliable rendering.
 */
export const createJiraCardDocument = (cardHtml: string): string => {
  const processed = transformFieldGrids(cardHtml);
  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8">
    <title>JIRA Report</title>
    <style>
      body { font-family: Arial, sans-serif; font-size: 12pt; line-height: 1.6; max-width: 820px; margin: 40px auto; padding: 20px; color: #111827; }
      a { color: #1d4ed8; text-decoration: none; }
      h1 { font-size: 18pt; margin-bottom: 16px; }
      h2 { font-size: 13pt; text-align:center;margin-bottom:16px;padding:10px;font-weight: 700; text-transform: uppercase; letter-spacing: .05em; padding: 6px 12px; margin: 24px 0 8px; border-radius: 4px; background: #f3f4f6; color: #1e3a5f; }
      h2.section-header-green  { color: #000000; padding: 4px;}
      h2.section-header-yellow { color: #000000; padding: 4px;}
      h2.section-header-red    { color: #000000; padding: 4px;}
      h3 { font-size: 12pt; font-weight: 600; margin: 12px 0 4px; }
      p  { margin: 4px 0; }
      .field-description { margin: 8px 0 16px; }
      @media print { body { margin: 0; padding: 20px; } }
    </style>
  </head>
  <body>
    <div class="jira-card-content" data-mode="light">
      ${processed}
    </div>
  </body>
</html>`;
};

/**
 * Download message content as document (PDF or DOCX)
 */
export const downloadMessageAsDocument = async (
  content: string,
  format: 'pdf' | 'docx',
  filename: string,
  title: string = 'Report'
): Promise<void> => {
  const fullDocument = isJiraIssueCardHtml(content)
    ? createJiraCardDocument(content)
    : createHTMLDocument(convertMarkdownToHTML(content), title);

  if (format === 'pdf') {
    await printAsPDF(fullDocument, filename);
  } else {
    downloadAsDocx(fullDocument, filename);
  }
};

/**
 * Download chat history as Word document
 */
export const downloadChatHistory = (
  chatHistory: any[],
  filename: string = `chat-history-${Date.now()}`,
  chartImagesMap?: Map<number, string[]>
): void => {
  try {
    let chatHTML = '';
    
    chatHistory.forEach((message: any, msgIndex: number) => {
      const role = message.role === 'user' ? 'User' : 'Assistant';
      let content = '';

      // For assistant messages with a Jira card in ui_events, use the card HTML directly
      if (
        message.role === 'assistant' &&
        message.ui_events &&
        message.ui_events.length > 0 &&
        message.ui_events[0].html
      ) {
        const cardHtml = message.ui_events[0].html;
        content = transformFieldGrids(cardHtml);
      } else if (typeof message.content === 'string') {
        content = convertMarkdownToHTML(message.content);
      } else if (Array.isArray(message.content)) {
        // Handle structured content
        content = message.content
          .map((item: any) => {
            if (item.type === 'input_text') return item.text || '';
            if (item.type === 'input_file') return `[File: ${item.filename}]`;
            return '';
          })
          .filter((text: string) => text.trim())
          .join('<br>');
        content = `<p>${content}</p>`;
      }
      
      // Append chart PNG images if captured for this message
      const chartImages = chartImagesMap?.get(msgIndex) ?? [];
      if (chartImages.length > 0) {
        content += chartImages
          .map((dataUrl: string, ci: number) =>
            `<div style="margin: 16px 0; page-break-inside: avoid;"><img src="${dataUrl}" width="624" height="auto" style="display:block; width:624px; max-width:100%; height:auto;" alt="Chart ${ci + 1}"/></div>`
          )
          .join('');
      }

      // Add message to chat history
      chatHTML += `<div style="margin-bottom: 20px;">
        <strong style="color: ${message.role === 'user' ? '#0066cc' : '#cc0000'};">${role}:</strong>
        <div style="margin-top: 5px;">${content}</div>
      </div>`;
    });
    
    // Create HTML document
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Chat History</title>
          <style>
            body { font-family: Arial, sans-serif; font-size: 12pt; line-height: 1.6; }
            pre { background-color: #f4f4f4; padding: 10px; border-radius: 4px; }
            code { background-color: #f4f4f4; padding: 2px 4px; border-radius: 2px; }
            table { border-collapse: collapse; width: 100%; margin: 10px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f4f4f4; }
            img { display: block; width: 624px; max-width: 100%; height: auto; margin: 0; padding: 0; }
          </style>
        </head>
        <body>
          <h1>Chat History - ${new Date().toLocaleDateString()}</h1>
          ${chatHTML}
        </body>
      </html>
    `;
    
    downloadAsDocx(htmlContent, filename);
  } catch (error) {
    console.error('Failed to download chat history:', error);
    throw error;
  }
};
